export interface CustomQuestion {
	id: string;
	question: string;
	required: boolean;
	type: "text" | "textarea" | "select" | "yesno";
	options?: string[];
}

export const COUNTRIES = [
	"Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria",
	"Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
	"Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
	"Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
	"Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
	"El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
	"Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
	"Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
	"Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan",
	"Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar",
	"Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia",
	"Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal",
	"Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan",
	"Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
	"Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
	"Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
	"South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan",
	"Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan",
	"Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City",
	"Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe",
];

export interface PositionConfig {
	requiresLinkedIn: boolean;
	requiresPortfolio: boolean;
	requiresPhone: boolean;
	showTwitter: boolean;
	hideResume?: boolean;
	portfolioLabel?: string;
	customQuestions: CustomQuestion[];
}

const positionFormConfig: Record<string, PositionConfig> = {
	"Head of Content": {
		requiresLinkedIn: true,
		requiresPortfolio: true,
		requiresPhone: false,
		showTwitter: true,
		portfolioLabel: "Writing samples or portfolio",
		customQuestions: [
			{ id: "best-work", question: "Link to something you wrote that you're proud of. What makes it good?", required: true, type: "textarea" },
			{ id: "content-pov", question: "What's a contrarian take you have about content marketing that most people get wrong?", required: true, type: "textarea" },
		],
	},
	"Visual Designer": {
		requiresLinkedIn: false,
		requiresPortfolio: true,
		requiresPhone: false,
		showTwitter: true,
		portfolioLabel: "Portfolio URL",
		customQuestions: [
			{ id: "years-experience", question: "Years of professional design experience", required: true, type: "text" },
			{ id: "ai-tools", question: "How do you use AI in your design workflow? Which tools, for what tasks, and how has it changed how you work?", required: true, type: "textarea" },
			{ id: "visual-taste", question: "Share a piece of visual design (not your own) that you think is exceptional. What makes it work?", required: true, type: "textarea" },
			{ id: "craft-example", question: "Walk us through a project where you pushed the visual craft further than the brief required. Why did you do it?", required: true, type: "textarea" },
		],
	},
	"Project Manager": {
		requiresLinkedIn: false,
		requiresPortfolio: false,
		requiresPhone: false,
		showTwitter: false,
		customQuestions: [
			{ id: "experience-confirm", question: "Do you have 4+ years of experience managing client accounts at an agency or studio?", required: true, type: "yesno" },
			{ id: "scope-creep", question: "A client keeps adding requests outside the original scope. How do you handle it without damaging the relationship?", required: true, type: "textarea" },
			{ id: "tough-conversation", question: "Tell us about a tough conversation you had with a client or team member. What was at stake and what did you do?", required: true, type: "textarea" },
			{ id: "references", question: "Can you provide 2 professional references from previous clients or managers? If yes, include their names and how to reach them.", required: true, type: "textarea" },
		],
	},
	"Senior Brand Designer": {
		requiresLinkedIn: false,
		requiresPortfolio: true,
		requiresPhone: false,
		showTwitter: true,
		portfolioLabel: "Portfolio URL",
		customQuestions: [
			{ id: "taste", question: "Share a brand you think has exceptional visual identity. What specifically makes it work?", required: true, type: "textarea" },
		],
	},
	"AI Builder": {
		requiresLinkedIn: false,
		requiresPortfolio: true,
		requiresPhone: false,
		showTwitter: true,
		portfolioLabel: "Portfolio, GitHub, or live project URL",
		customQuestions: [
			{ id: "ai-workflow", question: "Walk us through how you build something from scratch. What AI tools do you use, in what order, and where do you still write code manually?", required: true, type: "textarea" },
			{ id: "shipped-project", question: "Link to something you've shipped that was built primarily with AI tools. What was your role vs. what the AI did?", required: true, type: "textarea" },
			{ id: "debugging", question: "Describe a time AI-generated code broke in a way that was hard to diagnose. How did you find and fix the issue?", required: true, type: "textarea" },
			{ id: "tech-stack", question: "What's your go-to stack and which AI coding tools do you use daily?", required: true, type: "text" },
			{ id: "availability", question: "What timezone are you in, and are you available full-time during European business hours (CET ±2)?", required: true, type: "text" },
		],
	},
	"Virtual Assistant": {
		requiresLinkedIn: false,
		requiresPortfolio: false,
		requiresPhone: false,
		showTwitter: false,
		customQuestions: [
			{ id: "availability", question: "How many hours per week are you available? Are you open to increasing over time?", required: true, type: "text" },
			{ id: "timezone", question: "What timezone are you based in?", required: true, type: "text" },
			{ id: "judgment-call", question: "Describe a time you had to make a judgment call without being able to check in first. What happened?", required: true, type: "textarea" },
			{ id: "tools", question: "Which tools are you most comfortable with? (Google Workspace, Notion, Linear, Slack, etc.)", required: true, type: "text" },
		],
	},

	"Personal Assistant": {
		requiresLinkedIn: true,
		requiresPortfolio: false,
		requiresPhone: true,
		showTwitter: false,
		customQuestions: [
			{ id: "ambiguity", question: "Describe a time you had to make a judgment call without being able to check in first. What happened?", required: true, type: "textarea" },
			{ id: "prioritization", question: "You have three urgent requests come in at once. How do you decide what to handle first?", required: true, type: "textarea" },
		],
	},
	"Reels Editor": {
		requiresLinkedIn: false,
		requiresPortfolio: false,
		requiresPhone: false,
		showTwitter: false,
		hideResume: true,
		customQuestions: [
			{ id: "asia-confirm", question: "I confirm I live in Asia.", required: true, type: "yesno" },
			{ id: "availability", question: "Can you commit to uploading 20 Reels per day, every 24 hours, on a daily or set schedule?", required: true, type: "yesno" },
			{ id: "rate", question: "What is your expected rate? (Per hour or per batch of 20 Reels, in USD)", required: true, type: "text" },
		],
	},
	"BD LinkedIn": {
		requiresLinkedIn: true,
		requiresPortfolio: false,
		requiresPhone: false,
		showTwitter: false,
		customQuestions: [
			{ id: "metrics", question: "What's your typical connection acceptance rate and response rate on LinkedIn? How did you get there?", required: true, type: "textarea" },
			{ id: "personalization", question: "Walk us through how you'd research and approach a specific prospect. Use a real example if you can.", required: true, type: "textarea" },
			{ id: "best-win", question: "Tell us about a deal that seemed dead but you brought back to life. What did you do differently?", required: true, type: "textarea" },
		],
	},
};

export function getConfigForPosition(title: string): PositionConfig {
	return (
		positionFormConfig[title] || {
			requiresLinkedIn: false,
			requiresPortfolio: false,
			requiresPhone: false,
			showTwitter: false,
			customQuestions: [],
		}
	);
}
